import { API_BASE_URL } from './apiServices';
import {
    isAnalyticsSuppressedPath,
    isCurrentAnalyticsSuppressedPath
} from '../utils/privacyRoute';

const SESSION_STORAGE_KEY = 'ab_analytics_session_id';
const TRACKED_PAGE_EVENTS_KEY = 'ab_tracked_page_events';
const EVENTS_ENDPOINT = `${API_BASE_URL.replace(/\/+$/, '')}/site/events`;
const REDDIT_PIXEL_ID = `${process.env.VUE_APP_REDDIT_PIXEL_ID || ''}`.trim();
const REDDIT_PIXEL_SCRIPT_ID = 'ab-reddit-pixel-script';
const REDDIT_PIXEL_SCRIPT_URL = 'https://www.redditstatic.com/ads/pixel.js';
const DEFAULT_GA4_MEASUREMENT_ID = 'G-MF2VE3YDH1';
const GA4_SCRIPT_ID = 'ab-ga4-script';
const GA4_SCRIPT_BASE_URL = 'https://www.googletagmanager.com/gtag/js';
const GA4_HOSTS = ['animebookmark.com', 'www.animebookmark.com'];
const DEFAULT_X_PIXEL_ID = 'rd7sg';
const DEFAULT_X_SIGNUP_EVENT_ID = 'tw-rd7sg-rd8je';
const X_PIXEL_SCRIPT_ID = 'ab-x-pixel-script';
const X_PIXEL_SCRIPT_URL = 'https://static.ads-twitter.com/uwt.js';
const X_PIXEL_HOSTS = ['animebookmark.com', 'www.animebookmark.com'];
const REDDIT_COOKIE_KEYS = ['_rdt_uuid', 'rdt_uuid'];
const AD_ATTRIBUTION_STORAGE_KEY = 'ab_ad_attribution_v1';
const AD_ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const AD_ATTRIBUTION_QUERY_KEYS = {
    utmSource: 'utm_source',
    utmMedium: 'utm_medium',
    utmCampaign: 'utm_campaign',
    utmContent: 'utm_content',
    utmTerm: 'utm_term',
    redditClickId: 'rdt_cid'
};
const REDDIT_EVENT_MAP = {
    home_view: { trackingType: 'PageVisit' },
    season_page_view: { trackingType: 'PageVisit' },
    archive_page_view: { trackingType: 'PageVisit' },
    releases_page_view: { trackingType: 'PageVisit' },
    discover_page_view: { trackingType: 'PageVisit' },
    overview_view: { trackingType: 'ViewContent' },
    watch_order_view: { trackingType: 'ViewContent' },
    episode_view: { trackingType: 'ViewContent' },
    bookmark_add: { trackingType: 'AddToWishlist' },
    audience_capture_submit: { trackingType: 'Custom', customEventName: 'NewsletterSignup' },
    auth_sign_up: { trackingType: 'SignUp' },
    auth_sign_in: { trackingType: 'Custom', customEventName: 'SignIn' }
};

let redditPixelInitialized = false;
let googleAnalyticsInitialized = false;
let xPixelInitialized = false;
const deferredThirdPartyScriptIds = new Set();

const canUseBrowserApi = () => typeof window !== 'undefined';

const createRandomToken = () => {
    if (canUseBrowserApi() && window.crypto?.getRandomValues) {
        const values = new Uint32Array(2);
        window.crypto.getRandomValues(values);
        return Array.from(values).map((value) => value.toString(36)).join('');
    }

    return Math.random().toString(36).slice(2, 12);
};

const createSessionId = () => {
    const randomPart = createRandomToken().slice(0, 10);
    return `ab_${Date.now().toString(36)}_${randomPart}`;
};

export const createAnalyticsEventId = (prefix = 'evt') => {
    const normalizedPrefix = `${prefix || 'evt'}`
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 24) || 'evt';

    return `ab_${normalizedPrefix}_${Date.now().toString(36)}_${createRandomToken().slice(0, 12)}`;
};

export const getAnalyticsSessionId = () => {
    if (!canUseBrowserApi()) {
        return '';
    }

    let sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
        sessionId = createSessionId();
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }

    return sessionId;
};

const getCookieValue = (cookieName = '') => {
    if (!canUseBrowserApi() || !cookieName) {
        return '';
    }

    const escapedName = cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    if (!match) {
        return '';
    }

    try {
        return decodeURIComponent(match[1] || '');
    } catch (_error) {
        return match[1] || '';
    }
};

export const getRedditPixelUuid = () => {
    if (!canUseBrowserApi()) {
        return '';
    }

    for (const cookieName of REDDIT_COOKIE_KEYS) {
        const value = getCookieValue(cookieName);
        if (value) {
            return value.slice(0, 120);
        }
    }

    return '';
};

const normalizeAttributionValue = (value = '', maxLength = 180) => (
    typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
);

const normalizeRedditClickId = (value = '') => {
    const normalized = normalizeAttributionValue(value, 256);
    return /^[A-Za-z0-9._~-]+$/.test(normalized) ? normalized : '';
};

const normalizeAdAttribution = (value = {}) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const capturedAt = new Date(value.capturedAt || '').getTime();
    if (
        !Number.isFinite(capturedAt)
        || capturedAt > Date.now() + (5 * 60 * 1000)
        || capturedAt < Date.now() - AD_ATTRIBUTION_MAX_AGE_MS
    ) {
        return null;
    }

    const normalized = {
        utmSource: normalizeAttributionValue(value.utmSource),
        utmMedium: normalizeAttributionValue(value.utmMedium),
        utmCampaign: normalizeAttributionValue(value.utmCampaign),
        utmContent: normalizeAttributionValue(value.utmContent),
        utmTerm: normalizeAttributionValue(value.utmTerm),
        redditClickId: normalizeRedditClickId(value.redditClickId),
        landingPath: `${value.landingPath || ''}`.startsWith('/')
            ? `${value.landingPath}`.split('?')[0].slice(0, 240)
            : '/',
        capturedAt: new Date(capturedAt).toISOString()
    };

    if (!normalized.utmSource && normalized.redditClickId) {
        normalized.utmSource = 'reddit';
    }

    return Object.entries(AD_ATTRIBUTION_QUERY_KEYS).some(([key]) => normalized[key])
        ? normalized
        : null;
};

const readStoredAdAttribution = () => {
    if (!canUseBrowserApi()) {
        return null;
    }

    try {
        const attribution = normalizeAdAttribution(JSON.parse(window.localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY) || 'null'));
        if (!attribution) {
            window.localStorage.removeItem(AD_ATTRIBUTION_STORAGE_KEY);
        }
        return attribution;
    } catch (_error) {
        return null;
    }
};

const captureCurrentAdAttribution = () => {
    if (!canUseBrowserApi()) {
        return null;
    }

    const url = new URL(window.location.href);
    const currentValues = Object.entries(AD_ATTRIBUTION_QUERY_KEYS).reduce((result, [key, queryKey]) => {
        const value = queryKey === 'rdt_cid'
            ? normalizeRedditClickId(url.searchParams.get(queryKey) || '')
            : normalizeAttributionValue(url.searchParams.get(queryKey) || '');
        if (value) {
            result[key] = value;
        }
        return result;
    }, {});
    if (Object.keys(currentValues).length === 0) {
        return readStoredAdAttribution();
    }

    const attribution = normalizeAdAttribution({
        ...currentValues,
        landingPath: url.pathname || '/',
        capturedAt: new Date().toISOString()
    });
    if (!attribution) {
        return readStoredAdAttribution();
    }

    try {
        window.localStorage.setItem(AD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch (_error) {
        return attribution;
    }
    return attribution;
};

const buildSafeEventSourceUrl = (adAttribution = null) => {
    if (!canUseBrowserApi()) {
        return '';
    }

    const url = new URL(window.location.pathname || '/', window.location.origin);
    if (adAttribution?.redditClickId) {
        url.searchParams.set('rdt_cid', adAttribution.redditClickId);
    }
    return url.toString().slice(0, 600);
};

const getScreenDimension = (dimension = 'width') => {
    if (!canUseBrowserApi()) {
        return null;
    }

    const rawValue = Number(
        dimension === 'height'
            ? window.screen?.height || window.innerHeight || 0
            : window.screen?.width || window.innerWidth || 0
    );
    if (!Number.isFinite(rawValue) || rawValue <= 0) {
        return null;
    }

    return Math.round(rawValue);
};

export const getAnalyticsBrowserContext = () => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return {
            sessionId: '',
            redditUuid: '',
            screenWidth: null,
            screenHeight: null,
            pagePath: canUseBrowserApi() ? window.location.pathname || '/' : '/',
            eventSourceUrl: '',
            adAttribution: null
        };
    }

    const adAttribution = captureCurrentAdAttribution();
    return {
        sessionId: getAnalyticsSessionId(),
        redditUuid: getRedditPixelUuid(),
        screenWidth: getScreenDimension('width'),
        screenHeight: getScreenDimension('height'),
        pagePath: canUseBrowserApi() ? window.location.pathname || '/' : '/',
        eventSourceUrl: buildSafeEventSourceUrl(adAttribution),
        adAttribution
    };
};

const getTrackedPageEvents = () => {
    if (!canUseBrowserApi()) {
        return new Set();
    }

    try {
        const rawValue = window.sessionStorage.getItem(TRACKED_PAGE_EVENTS_KEY);
        const parsedValue = JSON.parse(rawValue || '[]');
        if (!Array.isArray(parsedValue)) {
            return new Set();
        }
        return new Set(parsedValue);
    } catch (_error) {
        return new Set();
    }
};

const saveTrackedPageEvents = (trackedEvents = new Set()) => {
    if (!canUseBrowserApi()) {
        return;
    }

    window.sessionStorage.setItem(TRACKED_PAGE_EVENTS_KEY, JSON.stringify(Array.from(trackedEvents)));
};

const ensureRedditQueue = () => {
    if (!canUseBrowserApi() || typeof window.rdt === 'function') {
        return;
    }

    const queuedPixel = function(...args) {
        if (queuedPixel.sendEvent) {
            queuedPixel.sendEvent.apply(queuedPixel, args);
            return;
        }
        queuedPixel.callQueue.push(args);
    };

    queuedPixel.callQueue = [];
    window.rdt = queuedPixel;
};

const getRuntimeHostname = () => {
    if (!canUseBrowserApi()) {
        return '';
    }

    return `${window.location?.hostname || ''}`.trim().toLowerCase();
};

const getConfiguredXPixelId = () => {
    const envPixelId = `${process.env.VUE_APP_X_PIXEL_ID || ''}`.trim();
    if (envPixelId) {
        return envPixelId;
    }

    return X_PIXEL_HOSTS.includes(getRuntimeHostname()) ? DEFAULT_X_PIXEL_ID : '';
};

const getConfiguredGa4MeasurementId = () => {
    const envMeasurementId = `${process.env.VUE_APP_GA4_MEASUREMENT_ID || ''}`.trim();
    if (envMeasurementId) {
        return envMeasurementId;
    }

    return GA4_HOSTS.includes(getRuntimeHostname()) ? DEFAULT_GA4_MEASUREMENT_ID : '';
};

const getConfiguredXSignupEventId = () => {
    const envEventId = `${process.env.VUE_APP_X_SIGNUP_EVENT_ID || ''}`.trim();
    if (envEventId) {
        return envEventId;
    }

    return getConfiguredXPixelId() === DEFAULT_X_PIXEL_ID ? DEFAULT_X_SIGNUP_EVENT_ID : '';
};

const getXEventMapping = (eventName = '') => {
    if (eventName !== 'auth_sign_up') {
        return null;
    }

    const eventId = getConfiguredXSignupEventId();
    return eventId ? { eventId } : null;
};

const ensureXQueue = () => {
    if (!canUseBrowserApi() || typeof window.twq === 'function') {
        return;
    }

    const queuedPixel = function(...args) {
        if (queuedPixel.exe) {
            queuedPixel.exe.apply(queuedPixel, args);
            return;
        }
        queuedPixel.queue.push(args);
    };

    queuedPixel.version = '1.1';
    queuedPixel.queue = [];
    window.twq = queuedPixel;
};

const ensureGoogleAnalyticsQueue = () => {
    if (!canUseBrowserApi()) {
        return;
    }

    window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function(...args) {
            window.dataLayer.push(args);
        };
    }
};

const scheduleAfterPageLoadIdle = (callback) => {
    if (!canUseBrowserApi() || typeof callback !== 'function') {
        return;
    }

    const scheduleIdle = () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(callback, { timeout: 2500 });
            return;
        }

        window.setTimeout(callback, 1200);
    };

    if (document.readyState === 'complete') {
        scheduleIdle();
        return;
    }

    window.addEventListener('load', scheduleIdle, { once: true });
};

const appendDeferredThirdPartyScript = ({
    id = '',
    src = ''
} = {}) => {
    if (!canUseBrowserApi() || !id || !src || document.getElementById(id) || deferredThirdPartyScriptIds.has(id)) {
        return;
    }

    deferredThirdPartyScriptIds.add(id);
    scheduleAfterPageLoadIdle(() => {
        if (isCurrentAnalyticsSuppressedPath()) {
            deferredThirdPartyScriptIds.delete(id);
            return;
        }
        if (document.getElementById(id)) {
            return;
        }

        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        document.head.appendChild(script);
    });
};

export const initializeRedditPixel = () => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return;
    }
    captureCurrentAdAttribution();
    if (!canUseBrowserApi() || !REDDIT_PIXEL_ID) {
        return;
    }

    ensureRedditQueue();

    appendDeferredThirdPartyScript({
        id: REDDIT_PIXEL_SCRIPT_ID,
        src: REDDIT_PIXEL_SCRIPT_URL
    });

    if (redditPixelInitialized) {
        return;
    }

    window.rdt('init', REDDIT_PIXEL_ID, {
        optOut: false,
        useDecimalCurrencyValues: true
    });
    redditPixelInitialized = true;
};

export const initializeGoogleAnalytics = () => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return '';
    }
    captureCurrentAdAttribution();
    const measurementId = getConfiguredGa4MeasurementId();
    if (!canUseBrowserApi() || !measurementId) {
        return '';
    }

    ensureGoogleAnalyticsQueue();

    appendDeferredThirdPartyScript({
        id: GA4_SCRIPT_ID,
        src: `${GA4_SCRIPT_BASE_URL}?id=${encodeURIComponent(measurementId)}`
    });

    if (googleAnalyticsInitialized) {
        return measurementId;
    }

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
        send_page_view: false
    });
    googleAnalyticsInitialized = true;
    return measurementId;
};

export const initializeXPixel = () => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return;
    }
    const pixelId = getConfiguredXPixelId();
    if (!canUseBrowserApi() || !pixelId) {
        return;
    }

    ensureXQueue();

    appendDeferredThirdPartyScript({
        id: X_PIXEL_SCRIPT_ID,
        src: X_PIXEL_SCRIPT_URL
    });

    if (xPixelInitialized) {
        return;
    }

    window.twq('config', pixelId);
    xPixelInitialized = true;
};

export const trackGoogleAnalyticsPageView = ({
    path = '',
    title = ''
} = {}) => {
    if (isAnalyticsSuppressedPath(path) || isCurrentAnalyticsSuppressedPath()) {
        return;
    }
    const measurementId = initializeGoogleAnalytics();
    if (!canUseBrowserApi() || !measurementId || typeof window.gtag !== 'function') {
        return;
    }

    const pagePath = path || `${window.location?.pathname || '/'}${window.location?.search || ''}${window.location?.hash || ''}`;
    const pageLocation = new URL(pagePath, window.location?.origin || 'https://animebookmark.com').toString();

    window.gtag('event', 'page_view', {
        page_title: title || document.title || 'AnimeBookmark',
        page_location: pageLocation,
        page_path: pagePath
    });
};

const sanitizeProperties = (properties = {}) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
        return {};
    }

    return Object.entries(properties).slice(0, 20).reduce((accumulator, [key, value]) => {
        if (!key) {
            return accumulator;
        }

        if (typeof value === 'string') {
            accumulator[key] = value.slice(0, 240);
            return accumulator;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            accumulator[key] = value;
            return accumulator;
        }

        if (Array.isArray(value)) {
            accumulator[key] = value.slice(0, 10).map((item) => {
                if (typeof item === 'string') {
                    return item.slice(0, 120);
                }
                if (typeof item === 'number' || typeof item === 'boolean') {
                    return item;
                }
                return null;
            }).filter((item) => item !== null);
            return accumulator;
        }

        if (value && typeof value === 'object') {
            accumulator[key] = Object.entries(value).slice(0, 8).reduce((nestedAccumulator, [nestedKey, nestedValue]) => {
                if (!nestedKey) {
                    return nestedAccumulator;
                }
                if (typeof nestedValue === 'string') {
                    nestedAccumulator[nestedKey] = nestedValue.slice(0, 120);
                } else if (typeof nestedValue === 'number' || typeof nestedValue === 'boolean') {
                    nestedAccumulator[nestedKey] = nestedValue;
                }
                return nestedAccumulator;
            }, {});
        }

        return accumulator;
    }, {});
};

const sanitizeGoogleAnalyticsParameterName = (key = '') => (
    `${key || ''}`
        .trim()
        .replace(/[^A-Za-z0-9_]+/g, '_')
        .replace(/^[^A-Za-z]+/, '')
        .slice(0, 40)
);

const sanitizeGoogleAnalyticsParameters = (parameters = {}) => (
    Object.entries(sanitizeProperties(parameters)).reduce((accumulator, [key, value]) => {
        const normalizedKey = sanitizeGoogleAnalyticsParameterName(key);
        if (!normalizedKey) {
            return accumulator;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            accumulator[normalizedKey] = value;
        }
        return accumulator;
    }, {})
);

const getGoogleAnalyticsSignupMethod = (properties = {}) => {
    const source = `${properties?.method || properties?.source || ''}`.trim().toLowerCase();
    if (source.includes('google')) {
        return 'google';
    }
    if (source.includes('password') || source.includes('email') || source.includes('credential')) {
        return 'password';
    }
    return 'unknown';
};

export const trackGoogleAnalyticsEvent = ({
    eventName = '',
    eventId = '',
    parameters = {}
} = {}) => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return '';
    }
    const normalizedEventName = `${eventName || ''}`.trim();
    const measurementId = initializeGoogleAnalytics();
    if (!canUseBrowserApi() || !measurementId || !normalizedEventName || typeof window.gtag !== 'function') {
        return '';
    }

    const payload = sanitizeGoogleAnalyticsParameters(parameters);
    const normalizedEventId = `${eventId || ''}`.trim().slice(0, 128);
    if (normalizedEventId) {
        payload.event_id = normalizedEventId;
    }

    try {
        window.gtag('event', normalizedEventName, payload);
    } catch (_error) {
        return normalizedEventId;
    }

    return normalizedEventId;
};

export const trackGoogleAnalyticsSignupEvent = ({
    eventId = '',
    method = '',
    properties = {}
} = {}) => trackGoogleAnalyticsEvent({
    eventName: 'sign_up',
    eventId,
    parameters: {
        method: `${method || getGoogleAnalyticsSignupMethod(properties) || 'unknown'}`.trim().toLowerCase().slice(0, 36)
    }
});

const getAnalyticsTitle = (properties = {}) => (
    `${properties?.title || properties?.animeTitle || properties?.franchiseTitle || ''}`.trim().slice(0, 120)
);

const getAnalyticsAnimeId = (properties = {}) => {
    const animeId = Number.parseInt(properties?.animeId, 10);
    return Number.isFinite(animeId) ? animeId : null;
};

const buildRedditPixelPayload = ({
    eventId = '',
    properties = {},
    mapping = null
} = {}) => {
    const payload = {};
    if (eventId) {
        payload.conversionId = eventId;
    }

    if (mapping?.trackingType === 'Custom' && mapping?.customEventName) {
        payload.customEventName = mapping.customEventName;
    }

    const animeId = getAnalyticsAnimeId(properties);
    const title = getAnalyticsTitle(properties);
    if (animeId !== null) {
        payload.id = `${animeId}`;
        payload.category = 'anime';
    }
    if (title) {
        payload.name = title;
    }

    return payload;
};

const buildXPixelPayload = ({
    eventId = '',
    properties = {}
} = {}) => {
    const payload = {};
    if (eventId) {
        payload.conversion_id = eventId;
    }

    const status = `${properties?.status || 'completed'}`.trim();
    if (status) {
        payload.status = status.slice(0, 40);
    }

    return payload;
};

export const trackRedditStandardEvent = ({
    eventName = '',
    eventId = '',
    properties = {}
} = {}) => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return '';
    }
    const mapping = REDDIT_EVENT_MAP[eventName];
    if (!mapping || !REDDIT_PIXEL_ID || !canUseBrowserApi()) {
        return '';
    }

    const resolvedEventId = eventId || createAnalyticsEventId(eventName);
    initializeRedditPixel();

    if (typeof window.rdt !== 'function') {
        return resolvedEventId;
    }

    try {
        window.rdt('track', mapping.trackingType, buildRedditPixelPayload({
            eventId: resolvedEventId,
            properties,
            mapping
        }));
    } catch (_error) {
        return resolvedEventId;
    }

    return resolvedEventId;
};

export const trackXStandardEvent = ({
    eventName = '',
    eventId = '',
    properties = {}
} = {}) => {
    if (isCurrentAnalyticsSuppressedPath()) {
        return '';
    }
    const mapping = getXEventMapping(eventName);
    if (!mapping || !getConfiguredXPixelId() || !canUseBrowserApi()) {
        return '';
    }

    const resolvedEventId = eventId || createAnalyticsEventId(eventName);
    initializeXPixel();

    if (typeof window.twq !== 'function') {
        return resolvedEventId;
    }

    try {
        window.twq('event', mapping.eventId, buildXPixelPayload({
            eventId: resolvedEventId,
            properties
        }));
    } catch (_error) {
        return resolvedEventId;
    }

    return resolvedEventId;
};

export const trackAdvertisingStandardEvent = ({
    eventName = '',
    eventId = '',
    properties = {}
} = {}) => {
    if (!eventName || isCurrentAnalyticsSuppressedPath()) {
        return '';
    }

    const resolvedEventId = eventId || createAnalyticsEventId(eventName);
    trackRedditStandardEvent({
        eventName,
        eventId: resolvedEventId,
        properties
    });
    trackXStandardEvent({
        eventName,
        eventId: resolvedEventId,
        properties
    });
    if (eventName === 'auth_sign_up') {
        trackGoogleAnalyticsSignupEvent({
            eventId: resolvedEventId,
            properties
        });
    }

    return resolvedEventId;
};

export const trackSiteEvent = ({
    eventName = '',
    pagePath = '',
    properties = {},
    source = 'frontend',
    eventId = '',
    skipReddit = false
} = {}) => {
    if (
        !canUseBrowserApi()
        || !eventName
        || !pagePath.startsWith('/')
        || isAnalyticsSuppressedPath(pagePath)
        || isCurrentAnalyticsSuppressedPath()
    ) {
        return '';
    }

    const resolvedEventId = eventId || createAnalyticsEventId(eventName);
    const browserContext = getAnalyticsBrowserContext();

    if (!skipReddit) {
        trackRedditStandardEvent({
            eventName,
            eventId: resolvedEventId,
            properties
        });
    }

    const payload = {
        eventName,
        eventId: resolvedEventId,
        sessionId: browserContext.sessionId,
        redditUuid: browserContext.redditUuid,
        screenWidth: browserContext.screenWidth,
        screenHeight: browserContext.screenHeight,
        eventSourceUrl: browserContext.eventSourceUrl,
        adAttribution: browserContext.adAttribution,
        pagePath,
        referrer: document.referrer || '',
        source,
        properties: sanitizeProperties(properties)
    };

    if (navigator?.sendBeacon) {
        const body = new Blob([JSON.stringify(payload)], {
            type: 'application/json'
        });
        navigator.sendBeacon(EVENTS_ENDPOINT, body);
        return resolvedEventId;
    }

    fetch(EVENTS_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive: true
    }).catch(() => {});

    return resolvedEventId;
};

export const trackPageEventOnce = ({
    key = '',
    eventName = '',
    pagePath = '',
    properties = {},
    source = 'frontend'
} = {}) => {
    if (
        !canUseBrowserApi()
        || !key
        || !eventName
        || isAnalyticsSuppressedPath(pagePath)
        || isCurrentAnalyticsSuppressedPath()
    ) {
        return;
    }

    const trackedEvents = getTrackedPageEvents();
    if (trackedEvents.has(key)) {
        return '';
    }

    trackedEvents.add(key);
    saveTrackedPageEvents(trackedEvents);
    return trackSiteEvent({
        eventName,
        pagePath,
        properties,
        source
    });
};
